import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { useIsOfferLocked } from '@/hooks/use-is-offer-locked';
import { useToast } from '@/hooks/use-toast';
import { useDealContext } from '@/lib/contexts/deal.context';
import {
  PricingRecomputeTarget as Target,
  PricingRecalculationStatus as Status,
  ValidationRuleId,
} from '@/lib/graphql/generated/graphql';
import { useRecomputeDealItemsPricing } from '@/lib/hooks/useRecomputeDealItemsPricing';
import {
  pricingCorrectionPendingAtom,
  type PricingRule,
} from '@/states/pricing-correction.state';
import { useAtom } from 'jotai';

const options = {
  [ValidationRuleId.DiscountConsistency]: [
    { target: Target.ChooseDiscount, field: 'chooseDiscount' },
    { target: Target.ListPrice, field: 'listPrice' },
    { target: Target.ChoosePriceFromDiscount, field: 'choosePrice' },
  ],
  [ValidationRuleId.PricingMarginConsistency]: [
    { target: Target.MarginPercentage, field: 'marginPercentage' },
    { target: Target.ChoosePriceFromMargin, field: 'choosePrice' },
    { target: Target.BuyingPrice, field: 'buyingPrice' },
  ],
} as const;

type Outcome = Awaited<
  ReturnType<ReturnType<typeof useRecomputeDealItemsPricing>['recompute']>
>;

export function PricingCorrectionGroup({
  ruleId,
  itemIds,
}: {
  ruleId: PricingRule;
  itemIds: string[];
}) {
  const { t } = useTranslation();
  const { deal } = useDealContext();
  const { toast } = useToast();
  const locked = useIsOfferLocked();
  const { recompute } = useRecomputeDealItemsPricing(deal.id);
  const [pending, setPending] = useAtom(pricingCorrectionPendingAtom);
  const [running, setRunning] = useState(false);
  const [target, setTarget] = useState<Target>(options[ruleId][0].target);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [failed, setFailed] = useState(false);
  const groupId = useId();
  const margin = ruleId === ValidationRuleId.PricingMarginConsistency;
  const failedResults =
    outcome?.results.filter(
      (item) =>
        (item.status === Status.Failed || item.status === Status.Skipped) &&
        item.reason
    ) ?? [];

  const apply = async () => {
    if (itemIds.length === 0 || pending || locked) return;
    setPending(true);
    setRunning(true);
    setFailed(false);
    setOutcome(null);
    try {
      const result = await recompute(itemIds, target);
      setOutcome(result);
      toast({
        title: t('pricing_correction.result_title'),
        description: t('pricing_correction.result', {
          updated: result.results.filter(
            (item) => item.status === Status.Updated
          ).length,
          unchanged: result.results.filter(
            (item) => item.status === Status.Unchanged
          ).length,
          failed: result.results.filter(
            (item) =>
              item.status === Status.Failed || item.status === Status.Skipped
          ).length,
        }),
      });
    } catch {
      setFailed(true);
    } finally {
      setPending(false);
      setRunning(false);
    }
  };

  return (
    <div className="space-y-3 rounded-md border p-3" aria-busy={running}>
      <fieldset disabled={pending || locked} className="space-y-2">
        <legend className="text-sm font-medium">
          {t('pricing_correction.choose_target')}
        </legend>
        {options[ruleId].map(({ target: option, field }, index) => (
          <label key={option} className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name={`${groupId}-target`}
              value={option}
              checked={target === option}
              onChange={() => setTarget(option)}
            />
            {t(`pricing_correction.fields.${field}`)}
            {index === 0 && (
              <span className="text-xs text-gray-500">
                {t('pricing_correction.recommended')}
              </span>
            )}
          </label>
        ))}
      </fieldset>
      <p className="text-xs text-gray-500">
        {t('pricing_correction.target_only')}
      </p>
      {margin && (
        <p className="text-xs text-gray-500">
          {t('pricing_correction.vat_hint')}
        </p>
      )}
      {locked && <p className="text-sm">{t('pricing_correction.locked')}</p>}
      <Button
        type="button"
        disabled={itemIds.length === 0 || pending || locked}
        onClick={apply}
      >
        {running
          ? t('pricing_correction.running')
          : t('pricing_correction.apply', { count: itemIds.length })}
      </Button>
      <div role="status" aria-live="polite" className="space-y-1 text-sm">
        {running && <p>{t('pricing_correction.running')}</p>}
        {outcome && (
          <>
            <p>
              {t('pricing_correction.result', {
                updated: outcome.results.filter(
                  (item) => item.status === Status.Updated
                ).length,
                unchanged: outcome.results.filter(
                  (item) => item.status === Status.Unchanged
                ).length,
                failed: outcome.results.filter(
                  (item) =>
                    item.status === Status.Failed ||
                    item.status === Status.Skipped
                ).length,
              })}
            </p>
            {failedResults.map(({ id, reason }) => (
              <p key={id} className="text-red-700">
                {t(`pricing_correction.reasons.${reason}`)}
              </p>
            ))}
            <p>
              {t(
                outcome.refreshed
                  ? 'pricing_correction.awaiting_validation'
                  : 'pricing_correction.refresh_failed'
              )}
            </p>
          </>
        )}
      </div>
      {failed && (
        <p role="alert" className="text-sm text-red-700">
          {t('pricing_correction.request_failed')}
        </p>
      )}
    </div>
  );
}
